import each from "lodash-es/each";
import every from "lodash-es/every";
import filter from "lodash-es/filter";
import find from "lodash-es/find";
import { PatientAnswer } from "./patient-answer";

export enum SurveyQuestionControlType {
  Checkboxes,
  DateString,
  FreeText,
  BinaryChoice,
  MultipleChoice,
  HorizontalMultipleChoice,
  Scale,
  Autocomplete,
  MultiSelect,
}

export class SurveyQuestion {
  id: number;
  name: string;
  focused: boolean;
  activated: boolean;
  order: number;
  controlType: SurveyQuestionControlType;
  allowMultipleAnswers: boolean;
  helperPrompt: string;
  placeholderAnswer: string;
  questionText: string;
  required: boolean;
  iconKey: string;
  questionContext: string;
  patientAnswers: PatientAnswer[];
  answers: SurveyAnswer[] = [];

  static fromApiV2(response): SurveyQuestion {
    const question = new SurveyQuestion();
    question.id = response.id;
    question.name = response.toc_header;
    question.order = response.order;
    question.allowMultipleAnswers = response.allow_multiple_answers;
    question.helperPrompt = response.helper_prompt;
    question.placeholderAnswer = response.placeholder_answer || "";
    question.questionText = response.question_text;
    question.required = response.required;
    question.iconKey = response.icon_key;
    question.questionContext = response.question_context;

    each(response.answers, answer => {
      question.answers.push(SurveyAnswer.fromApiV2(answer));
    });

    question.controlType = question.selectControlType();
    question.patientAnswers = question.createPatientAnswers();

    return question;
  }

  private selectControlType() {
    const answers = filter(this.answers, answer => !answer.isCustom);
    if (answers.length == 1) {
      if (answers[0].kind == "free_text") {
        return SurveyQuestionControlType.FreeText;
      } else if (answers[0].kind == "date_string") {
        return SurveyQuestionControlType.DateString;
      }
    } else if (every(answers, answer => answer.kind == "radio")) {
      if (answers.length == 2) {
        return SurveyQuestionControlType.BinaryChoice;
      } else {
        return SurveyQuestionControlType.MultipleChoice;
      }
    } else if (every(answers, answer => answer.kind == "horizontal_radio")) {
      return SurveyQuestionControlType.HorizontalMultipleChoice;
    } else if (every(answers, answer => answer.kind == "scale")) {
      return SurveyQuestionControlType.Scale;
    } else if (every(answers, answer => answer.kind == "checkbox") && this.allowMultipleAnswers) {
      return SurveyQuestionControlType.Checkboxes;
    } else if (
      every(
        answers,
        answer => answer.kind == "select" || answer.kind == "free_text" || answer.kind == "checkbox"
      )
    ) {
      return SurveyQuestionControlType.Autocomplete;
    }
  }

  private createPatientAnswers() {
    const patientAnswers = [];
    let patientAnswer;

    switch (this.controlType) {
      case SurveyQuestionControlType.BinaryChoice:
      case SurveyQuestionControlType.MultipleChoice:
      case SurveyQuestionControlType.HorizontalMultipleChoice:
      case SurveyQuestionControlType.Scale:
        patientAnswer = new PatientAnswer();
        patientAnswer.surveyQuestionId = this.id;
        patientAnswers.push(patientAnswer);
        break;
      case SurveyQuestionControlType.DateString:
        patientAnswer = new PatientAnswer();
        patientAnswer.surveyQuestionId = this.id;
        patientAnswer.isCustom = true;
        patientAnswer.kind = "date_string";
        patientAnswers.push(patientAnswer);
        break;
      case SurveyQuestionControlType.FreeText:
        patientAnswer = new PatientAnswer();
        patientAnswer.surveyQuestionId = this.id;
        patientAnswer.isCustom = true;
        patientAnswer.kind = "free_text";
        patientAnswers.push(patientAnswer);
        break;
      case SurveyQuestionControlType.Autocomplete:
      case SurveyQuestionControlType.Checkboxes:
        break;
    }

    return patientAnswers;
  }

  getDependentQuestion() {
    const selectedAnswer = find(this.answers, (answer: SurveyAnswer) => {
      return !!find(this.patientAnswers, (patientAnswer: PatientAnswer) => {
        return answer.id == patientAnswer.id && answer.dependentQuestion != null;
      });
    });
    return selectedAnswer && selectedAnswer.dependentQuestion;
  }

  isQuestionActivated() {
    return this.activated;
  }

  setFocus() {
    this.focused = true;
  }
}

export class SurveyAnswer {
  id: number;
  answerText: string;
  customPlaceholder: boolean;
  isCustom: boolean;
  kind: string;
  respawn: boolean;
  singletonAnswer: boolean;
  dependentQuestion: SurveyQuestion;

  static fromApiV2(response): SurveyAnswer {
    const answer = new SurveyAnswer();
    answer.id = response.id;
    answer.answerText = response.answer_text;
    answer.customPlaceholder = response.custom_placeholder;
    answer.isCustom = response.is_custom;
    answer.kind = response.kind;
    answer.respawn = response.respawn;
    answer.singletonAnswer = response.singleton_answer;
    if (response.dependent_question) {
      answer.dependentQuestion = SurveyQuestion.fromApiV2(response.dependent_question);
    }

    return answer;
  }
}
